import config from 'constants/config'
import { createSelector } from 'reselect'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { NAVBAR_SPECIFIC_ORDER_REGIONS_LIST } from 'constants/config/region'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { getNavItemsFlat } from 'constants/config/navItemsByRegion'
import { getAgentHubAffiliationRules } from 'agentHub/selectors/agentHubSelectors'
import { LabFeatureFlags } from 'constants/lab'
import { isLabSiteTakeoverPayload, isTakeoverId } from 'lib/lab/dynamicPayload'
import { takeovers } from 'takeovers/takeoverRegistry'
import { isLEStaff } from './accountSelectors'
import { getLocaleLanguageCode } from 'i18n/util/locale'

export const MALDIVES_BUNDLES_NAV_ID = 'nav-link-maldives-packages'

export const getNavItems = createSelector(
  (state: App.State) => state.auth.account,
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => getIsLoyaltyEnabled(state),
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.geo.locale,
  (state: App.State) => state.system.storeMode,
  (state: App.State) => state.optimizely.optimizelyFeatureFlags,
  (state: App.State) => getAgentHubAffiliationRules(state),
  (state: App.State) => state.system.features.labFlags,
  (state: App.State) => state.geo.geoStateName,
  (state: App.State) => isLEStaff(state),
  (account, isLuxPlusEnabled, isLoyaltyEnabled, currentRegionCode, locale, isStoreMode, featureFlags, affiliationRules, labFlags, geoStateName, isStaff) => {
    const enabledForAgenthubAgent = (nav: App.NavItem) => {
      if (nav.id === 'nav-link-car-hire') {
        return affiliationRules.canBookCarHire
      }

      if (nav.id === 'nav-link-cruises') {
        return affiliationRules.canBookCruise
      }

      return true
    }

    const navEnabled = (nav: App.NavItem) => {
      if (nav.onlyOnRegions && !nav.onlyOnRegions.includes(currentRegionCode)) {
        return false
      }

      if (nav.notOnRegions?.includes(currentRegionCode)) {
        return false
      }

      if (nav.onlyOnLanguages && !nav.onlyOnLanguages.includes(getLocaleLanguageCode(locale))) {
        return false
      }

      if (geoStateName && nav.notOnGeoStates?.includes(geoStateName)) {
        return false
      }

      if (nav.onlyOnGeoStates && (!geoStateName || !nav.onlyOnGeoStates.includes(geoStateName))) {
        return false
      }

      if (nav.featureToggle && !config[nav.featureToggle]) {
        return false
      }

      if (nav.optimizelyFeatureToggle && !featureFlags[nav.optimizelyFeatureToggle]) {
        return false
      }

      if (nav.accountToggle && (!account[nav.accountToggle])) {
        return false
      }

      if (nav.staffOnly && !isStaff) {
        return false
      }

      if (!!nav.luxPlusToggle && !isLuxPlusEnabled) {
        return false
      }

      if (!!nav.loyaltyToggle && !isLoyaltyEnabled) {
        return false
      }

      if (nav.hideInStoreMode && isStoreMode) {
        return false
      }

      if (nav.labCampaignFeatureToggle) {
        const takeoverRegions = isTakeoverId(nav.labCampaignFeatureToggle) ? takeovers[nav.labCampaignFeatureToggle]?.regions : undefined
        if (takeoverRegions && !takeoverRegions.has(currentRegionCode)) {
          return false
        }
        const labFlag = labFlags[LabFeatureFlags.enableSiteTakeover]
        if (labFlag?.enabled) {
          const payload = labFlag.value
          if (isLabSiteTakeoverPayload(payload)) {
            return payload.id === nav.labCampaignFeatureToggle
          }
        }
        return false
      }

      if (nav.id && !enabledForAgenthubAgent(nav)) {
        return false
      }

      return true
    }

    const navRegionConfig = NAVBAR_SPECIFIC_ORDER_REGIONS_LIST.has(currentRegionCode) ? currentRegionCode : 'DEFAULT'
    const navItems = Array.isArray(config.header.nav.items) ? config.header.nav.items : config.header.nav.items[navRegionConfig]
    const filteredItems = navItems.reduce<Array<App.NavItem>>((navList, nav) => {
      if (nav.newLabelOnlyOnRegions) nav.newLabel = nav.newLabelOnlyOnRegions.includes(currentRegionCode)

      if (nav.hasDropdown) {
        // need to whittle down the dropdown items of this subnav
        // note: subnavs can only go 1 level deep at most

        const subNav = {
          ...nav,
          items: nav.items?.filter(navEnabled),
        }
        if (subNav.items && subNav.items.length > 0) {
          navList.push(subNav)
        }
      } else if (navEnabled(nav)) {
        // normal nav, see if we can push it in
        navList.push(nav)
      }
      return navList
    }, [])
    return filteredItems
  },
)

/**
 * Retrieves the nav items flattened
 * This is used in the LuxKit navbar
 */
export const getFlatNavItems = createSelector(
  (state: App.State) => state.auth.account,
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => getIsLoyaltyEnabled(state),
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.geo.locale,
  (state: App.State) => state.system.storeMode,
  (state: App.State) => state.optimizely.optimizelyFeatureFlags,
  (state: App.State) => state.system.features.labFlags,
  (state: App.State) => state.geo.geoStateName,
  (state: App.State) => isLEStaff(state),
  (account, isLuxPlusEnabled, isLoyaltyEnabled, currentRegionCode, locale, isStoreMode, featureFlags, labFlags, geoStateName, isStaff): Array<App.NavItem> => {
    const updateHomesNavItemForAU = (navItem: App.NavItem): App.NavItem => {
      if (currentRegionCode !== 'AU' || navItem.id !== 'nav-link-homes-and-villas') {
        return navItem
      }
      return {
        ...navItem,
        to: '/rentals',
      }
    }
    const filteredItems = getNavItemsFlat().filter(nav => {
      if (nav.onlyOnRegions && !nav.onlyOnRegions.includes(currentRegionCode)) {
        return false
      }

      if (nav.notOnRegions?.includes(currentRegionCode)) {
        return false
      }

      if (nav.onlyOnLanguages && !nav.onlyOnLanguages.includes(getLocaleLanguageCode(locale))) {
        return false
      }

      if (geoStateName && nav.notOnGeoStates?.includes(geoStateName)) {
        return false
      }

      if (nav.onlyOnGeoStates && (!geoStateName || !nav.onlyOnGeoStates.includes(geoStateName))) {
        return false
      }

      if (nav.featureToggle && !config[nav.featureToggle]) {
        return false
      }

      if (nav.optimizelyFeatureToggle && !featureFlags[nav.optimizelyFeatureToggle]) {
        return false
      }

      if (nav.accountToggle && (!account[nav.accountToggle])) {
        return false
      }

      if (nav.staffOnly && !isStaff) {
        return false
      }

      if (!!nav.luxPlusToggle && !isLuxPlusEnabled) {
        return false
      }

      if (!!nav.loyaltyToggle && !isLoyaltyEnabled) {
        return false
      }

      if (nav.hideInStoreMode && isStoreMode) {
        return false
      }

      if (nav.labCampaignFeatureToggle) {
        const takeoverRegions = isTakeoverId(nav.labCampaignFeatureToggle) ? takeovers[nav.labCampaignFeatureToggle]?.regions : undefined
        if (takeoverRegions && !takeoverRegions.has(currentRegionCode)) {
          return false
        }
        const labFlag = labFlags[LabFeatureFlags.enableSiteTakeover]
        if (labFlag?.enabled) {
          const payload = labFlag.value
          if (isLabSiteTakeoverPayload(payload)) {
            return payload.id === nav.labCampaignFeatureToggle
          }
        }
        return false
      }

      return true
    },
    )
      .map(updateHomesNavItemForAU)
    return filteredItems
  },
)
